import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";

export const Reveal = ({ children, delay = 0, y = 24, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: reduce ? 0 : y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

export const Corners = ({ className = "border-arc-electric/70" }) => (
  <>
    <span aria-hidden="true" className={`absolute top-0 left-0 w-3 h-3 border-t border-l ${className}`} />
    <span aria-hidden="true" className={`absolute top-0 right-0 w-3 h-3 border-t border-r ${className}`} />
    <span aria-hidden="true" className={`absolute bottom-0 left-0 w-3 h-3 border-b border-l ${className}`} />
    <span aria-hidden="true" className={`absolute bottom-0 right-0 w-3 h-3 border-b border-r ${className}`} />
  </>
);

export const GlowButton = ({ children, href, to, onClick, variant = "primary", className = "", testId, cursor = "TARGET", type }) => {
  const base = "clip-btn btn-scan inline-flex items-center justify-center gap-2 px-6 py-3 font-mono text-[11px] font-semibold tracking-[0.25em] uppercase transition-colors duration-300";
  const styles =
    variant === "primary"
      ? "bg-arc-blue text-arc-white border border-arc-electric/70 hover:bg-arc-electric hover:text-arc-black"
      : variant === "ghost"
      ? "bg-transparent text-arc-silver border border-arc-silver/25 hover:border-arc-electric hover:text-arc-cyan"
      : "bg-arc-metal/60 text-arc-cyan border border-arc-cyan/40 hover:border-arc-cyan";
  const cls = `${base} ${styles} ${className}`;
  if (href) return <a data-testid={testId} data-cursor={cursor} href={href} target="_blank" rel="noopener noreferrer" className={cls}>{children}</a>;
  if (to) return <Link data-testid={testId} data-cursor={cursor} to={to} className={cls}>{children}</Link>;
  return <button type={type || "button"} data-testid={testId} data-cursor={cursor} onClick={onClick} className={cls}>{children}</button>;
};

export const Tag = ({ children, tone = "blue" }) => {
  const tones = {
    blue: "border-arc-electric/40 text-arc-cyan",
    green: "border-arc-green/40 text-arc-green",
    silver: "border-arc-silver/30 text-arc-silver/80",
    purple: "border-arc-purple/40 text-arc-purple",
    amber: "border-amber-400/40 text-amber-300",
  };
  return (
    <span className={`inline-block px-2 py-0.5 border font-mono text-[10px] tracking-[0.2em] uppercase ${tones[tone]}`}>
      {children}
    </span>
  );
};

export const StatusDot = ({ status }) => {
  const color =
    status === "ONLINE" ? "bg-arc-green" : status === "BUSY" || status === "PROCESSING" ? "bg-amber-400" : status === "OPEN" ? "bg-arc-cyan" : "bg-arc-silver/50";
  return <span className={`inline-block w-1.5 h-1.5 ${color} anim-pulse-dot`} aria-hidden="true" />;
};

export const SectionHeader = ({ index, title, subtitle, align = "left" }) => (
  <Reveal className={`mb-12 ${align === "center" ? "text-center" : ""}`}>
    {index && <div className="eyebrow mb-3" data-testid="section-index">{index}</div>}
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-arc-white uppercase whitespace-pre-line">{title}</h2>
    {subtitle && <p className="mt-4 max-w-2xl text-sm sm:text-base text-arc-silver/70 leading-relaxed">{subtitle}</p>}
  </Reveal>
);

export const HUDPanel = ({ title, rows, className = "", testId }) => (
  <div data-testid={testId} className={`hud-panel relative p-4 ${className}`}>
    <Corners />
    <div className="font-mono text-[10px] tracking-[0.3em] text-arc-cyan mb-3 uppercase">{title}</div>
    <div className="space-y-1.5">
      {rows.map(([k, v, tone]) => (
        <div key={k} className="flex items-center justify-between font-mono text-xs">
          <span className="text-arc-silver/50 tracking-wider">{k}</span>
          <span className={tone === "green" ? "text-arc-green" : tone === "cyan" ? "text-arc-cyan" : "text-arc-silver"}>{v}</span>
        </div>
      ))}
    </div>
  </div>
);

export const PageHead = ({ eyebrow, title, subtitle, note }) => (
  <header className="relative pt-32 pb-12 px-5 sm:px-8 max-w-7xl mx-auto">
    <Reveal>
      <div className="eyebrow mb-4">{eyebrow}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-arc-white uppercase">{title}</h1>
      {subtitle && <p className="mt-5 max-w-2xl text-base sm:text-lg text-arc-silver/70">{subtitle}</p>}
      {note && <div className="mt-5"><Tag tone="silver">{note}</Tag></div>}
    </Reveal>
  </header>
);
